import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { OnboardingChecklistService } from '@app/shared/onboarding/onboarding-checklist.service';
import { AuthService } from '@reducers/auth/auth.service';
import { ButtonComponent } from '@sb/ui/button';
import { LaddaDirective } from '@sb/ui/ladda';
import {
  BuyerIntent,
  BuyerRole,
  IndustryGroup,
  IntentUrgency,
  SpiceCriticalEvent,
  SpiceImpact,
  SpicePain,
  SpiceSituation,
} from '@shiftbase-com/ts-shared/models';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { filter, finalize, first, map, skipWhile, switchMap } from 'rxjs';

import {
  getRandomElement,
  MOCK_COMPANY_NAMES,
  MOCK_ESTIMATED_USERS,
  MOCK_FIRST_NAMES,
  MOCK_LAST_NAMES,
} from './dev-tools-mock-data';
import { DevToolsSectionComponent } from './dev-tools-section.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dev-tools-signup-onboarding',
  template: `
    <app-dev-tools-section title="Signup & Onboarding" storageKey="signup-onboarding">
      <div class="flex flex-wrap gap-2">
        <button sb-button size="sm" [disabled]="!isOnSignup()" (click)="skipSignupQuestions()" [sbLadda]="isLoading()">
          Skip Signup Questions
        </button>
        <button sb-button size="sm" [disabled]="!isOnOnboarding()" (click)="skipOnboarding()">Skip Onboarding</button>
      </div>
    </app-dev-tools-section>
  `,
  imports: [ButtonComponent, LaddaDirective, DevToolsSectionComponent],
})
export class DevToolsSignupOnboardingComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private onboardingChecklist = inject(OnboardingChecklistService);
  private destroyRef = inject(DestroyRef);

  isLoading = signal(false);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  isOnSignup = () => this.currentUrl()?.startsWith('/signup') ?? false;
  isOnOnboarding = () => this.currentUrl()?.startsWith('/onboarding') ?? false;

  skipSignupQuestions(): void {
    if (this.isLoading()) {
      return;
    }

    const signupPayload = this.buildSignupPayload();

    this.isLoading.set(true);

    this.recaptchaV3Service
      .execute('signupAction')
      .pipe(
        first(),
        skipWhile((token) => !token),
        switchMap((token) => this.authService.signUpNoVerify({ ...signupPayload, recaptchaToken: token })),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.onboardingChecklist.initGuide();
          this.router.navigate(['/onboarding']);
        },
        error: (error) => {
          console.error('DevTools: Signup failed', error);
        },
      });
  }

  /**
   * Builds the signup payload with mock data, matching the structure
   * expected by the signup endpoint.
   */
  private buildSignupPayload() {
    const firstName = getRandomElement(MOCK_FIRST_NAMES);
    const lastName = getRandomElement(MOCK_LAST_NAMES);

    return {
      Account: {
        locale: DefaultLocale,

        // Basic form data
        first_name: firstName,
        last_name: lastName,
        email: 'manager@shiftbase.com',
        phone_nr: '+31101234567',
        password: 'Foobarbarbaz1!',
        accept_conditions: true,
        marketing_opt_in: false,

        // Business form data
        company: getRandomElement(MOCK_COMPANY_NAMES),
        country: 'NL',
        estimated_users: getRandomElement(MOCK_ESTIMATED_USERS),

        // Business details form data
        industry: 'Information Technology',
        industry_group: IndustryGroup.SERVICES,
        buyer_role: BuyerRole.MANAGER,

        // SPICE form data
        situation: SpiceSituation.SPREADSHEETS,
        pain: SpicePain.NOTHING,
        impact: [SpiceImpact.TIME_WASTED_SCHEDULING, SpiceImpact.LABOUR_COSTS],
        critical_event: SpiceCriticalEvent.RAPID_GROWTH,
        otherCriticalEvent: '',

        // Buyer intent
        buyer_intent: [BuyerIntent.SCHEDULE, BuyerIntent.TIMETRACKING],
        intent_urgency: IntentUrgency.IMMEDIATELY,
      },
    };
  }

  skipOnboarding(): void {
    this.router.navigate(['/dashboard/my-overview']);
  }
}
